import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BundleTable } from './export/bundle';
import { formatValue } from './export/bundle';
import { ChartView } from './ChartView';
import { chartData, pivotData, tableData, type Words } from './engine';
import type { ChartVisual, PivotVisual, ReportDoc, TableVisual, TextVisual, Visual } from './model';

export interface TableSelection {
  visualId: string;
  rows: Set<number>;
  cols: Set<number>;
}

export interface BodyProps {
  doc: ReportDoc;
  visual: Visual;
  locale: string;
  words: Words & { total: string };
  originLabels: Record<string, string>;
  selection: TableSelection | null;
  onSelection: (s: TableSelection | null) => void;
  onText: (id: string, text: string) => void;
}

/** What a card on the canvas shows. */
export const VisualBody: React.FC<BodyProps> = (p) => {
  const { visual } = p;
  switch (visual.kind) {
    case 'table':
      return <TableBody {...p} visual={visual} />;
    case 'pivot':
      return <PivotBody {...p} visual={visual} />;
    case 'chart':
      return <ChartBody {...p} visual={visual} />;
    case 'map':
      return <MapBody doc={p.doc} />;
    case 'text':
      return <TextBody visual={visual} onText={p.onText} />;
    default:
      return null;
  }
};

const TableBody: React.FC<BodyProps & { visual: TableVisual }> = ({ doc, visual, locale, originLabels, selection, onSelection }) => {
  // The data only, not the whole report: moving a card changes the report, not what a table shows.
  const datasets = doc.datasets;
  const table = useMemo(() => tableData({ datasets }, visual, originLabels), [datasets, visual, originLabels]);
  const sel = selection?.visualId === visual.id ? selection : null;
  const toggle = (kind: 'rows' | 'cols', i: number) => {
    const next: TableSelection = sel ? { visualId: visual.id, rows: new Set(sel.rows), cols: new Set(sel.cols) } : { visualId: visual.id, rows: new Set(), cols: new Set() };
    const set = next[kind];
    if (set.has(i)) set.delete(i);
    else set.add(i);
    onSelection(next.rows.size || next.cols.size ? next : null);
  };
  return <Grid table={table} locale={locale} selectable sel={sel} onToggle={toggle} />;
};

const PivotBody: React.FC<BodyProps & { visual: PivotVisual }> = ({ doc, visual, locale, words, originLabels }) => {
  const datasets = doc.datasets;
  const table = useMemo(() => pivotData({ datasets }, visual, words, originLabels), [datasets, visual, words, originLabels]);
  return <Grid table={table} locale={locale} />;
};

const ChartBody: React.FC<BodyProps & { visual: ChartVisual }> = ({ doc, visual, locale, words, originLabels }) => {
  const { t } = useTranslation();
  const datasets = doc.datasets;
  const data = useMemo(() => chartData({ datasets }, visual, words, originLabels), [datasets, visual, words, originLabels]);
  return (
    <div className="w-full h-full p-1.5">
      <ChartView type={visual.chartType} data={data} locale={locale} showLabels={visual.showLabels} empty={t('studio.chart_empty')} />
    </div>
  );
};

const MapBody: React.FC<{ doc: ReportDoc }> = ({ doc }) => {
  const { t } = useTranslation();
  if (!doc.map) return <div className="h-full flex items-center justify-center text-[11px] text-gn-muted">{t('studio.map_none')}</div>;
  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 min-h-0 flex items-center justify-center bg-gn-tertiary">
        <img src={doc.map.image} alt="" className="max-w-full max-h-full object-contain" draggable={false} />
      </div>
      {doc.map.layers.length > 0 && (
        <div className="px-2 py-1 text-[10px] text-gn-secondary truncate" title={doc.map.layers.join(', ')}>
          {t('studio.map_layers')}: {doc.map.layers.join(', ')}
        </div>
      )}
    </div>
  );
};

const TextBody: React.FC<{ visual: TextVisual; onText: (id: string, text: string) => void }> = ({ visual, onText }) => {
  const { t } = useTranslation();
  return (
    <textarea
      className="w-full h-full resize-none bg-transparent text-[12px] leading-relaxed text-gn-primary p-2 focus:outline-none"
      value={visual.text}
      placeholder={t('studio.text_placeholder')}
      onChange={(e) => onText(visual.id, e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
};

/**
 * What a card shows of a table at most. A layer's features can be thousands of rows of hundreds of columns; drawn
 * whole, a few such cards stop the page. The PDF and the Excel workbook still get every row and column.
 */
const SHOWN_ROWS = 100;
const SHOWN_COLUMNS = 30;

type GridProps = {
  table: BundleTable;
  locale: string;
  selectable?: boolean;
  sel?: TableSelection | null;
  onToggle?: (kind: 'rows' | 'cols', i: number) => void;
};

/** A table as the studio shows it: header fixed, totals bold; with boxes to pick rows and columns for a chart. */
const GridView: React.FC<GridProps> = ({ table: whole, locale, selectable, sel, onToggle }) => {
  const { t } = useTranslation();
  const table = useMemo(() => {
    if (whole.rows.length <= SHOWN_ROWS && whole.columns.length <= SHOWN_COLUMNS) return whole;
    const totalRows = (whole.totalRows ?? []).filter((i) => i < SHOWN_ROWS);
    return {
      ...whole,
      columns: whole.columns.slice(0, SHOWN_COLUMNS),
      rows: whole.rows.slice(0, SHOWN_ROWS).map((r) => r.slice(0, SHOWN_COLUMNS)),
      totalRows,
    };
  }, [whole]);
  const moreRows = whole.rows.length - table.rows.length;
  const moreColumns = whole.columns.length - table.columns.length;
  const totals = new Set(table.totalRows ?? []);
  if (table.rows.length === 0) return <div className="h-full flex items-center justify-center text-[11px] text-gn-muted">{t('studio.no_rows')}</div>;
  return (
    <div className="w-full h-full overflow-auto" onMouseDown={(e) => e.stopPropagation()}>
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr>
            {selectable && <th className="sticky top-0 z-10 bg-gn-secondary w-6" />}
            {table.columns.map((c, i) => (
              <th key={c.key} className="sticky top-0 z-10 bg-gn-secondary px-2 py-1 text-left font-medium text-gn-secondary whitespace-nowrap border-b border-gn">
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  {selectable && i > 0 && (
                    <input type="checkbox" className="accent-blue-500" checked={!!sel?.cols.has(i)} onChange={() => onToggle?.('cols', i)} />
                  )}
                  <span className={c.format !== 'text' ? 'ml-auto' : ''}>{c.label}</span>
                </label>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <tr key={ri} className={`border-b border-gn-light ${totals.has(ri) ? 'font-semibold bg-gn-tertiary' : ri % 2 ? 'bg-gn-tertiary/40' : ''} ${sel?.rows.has(ri) ? '!bg-gn-blue-light' : ''}`}>
              {selectable && (
                <td className="px-1 text-center">
                  <input type="checkbox" className="accent-blue-500" checked={!!sel?.rows.has(ri)} onChange={() => onToggle?.('rows', ri)} />
                </td>
              )}
              {r.map((v, ci) => (
                <td key={ci} className={`px-2 py-0.5 whitespace-nowrap text-gn-primary ${typeof v === 'number' ? 'text-right tabular-nums' : ''} ${sel?.cols.has(ci) ? 'bg-gn-blue-light' : ''}`}>
                  {formatValue(v, table.columns[ci]?.format ?? 'text', locale)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(moreRows > 0 || moreColumns > 0) && (
        <div className="px-2 py-1 text-[10px] text-gn-muted">
          {t('studio.grid_more', { rows: whole.rows.length, columns: whole.columns.length, shownRows: table.rows.length, shownColumns: table.columns.length })}
        </div>
      )}
    </div>
  );
};

/** Drawn again only when what it shows changes, not each time another card moves. */
export const Grid = React.memo(GridView, (a, b) =>
  a.table === b.table && a.locale === b.locale && a.selectable === b.selectable && a.sel === b.sel);
